import { FC, ReactNode, useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import cx from "classnames";
import styles from "./GImage.module.scss";

interface GImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down" | "initial";
  threshold?: number;
  triggerOnce?: boolean;
  rootMargin?: string;
  fallback?: ReactNode;
  showSkeleton?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

const GImage: FC<GImageProps> = ({
  src,
  alt,
  className,
  style,
  width,
  height,
  objectFit = "cover",
  threshold = 0.1,
  triggerOnce = true,
  rootMargin = "50px",
  fallback,
  showSkeleton = true,
  onLoad,
  onError,
}) => {
  const { ref, inView } = useInView({
    triggerOnce,
    threshold,
    rootMargin,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageStyle = {
    objectFit,
    width,
    height,
    ...style,
  };

  // Reset loading state when src changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setImageLoaded(false);
  }, [src]);

  const handleImageLoad = () => {
    console.log("GImage: Image loaded successfully", src);
    setIsLoading(false);
    setImageLoaded(true);
    onLoad?.();
  };

  const handleImageError = () => {
    console.log("GImage: Image failed to load", src);
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const renderSkeleton = () => {
    if (!showSkeleton) return null;

    return (
      <div
        className={cx(styles.skeleton, styles.fallback)}
        style={{
          width: width || "100%",
          height: height || "200px",
          backgroundColor: "#ffffff",
        }}
      />
    );
  };

  const renderFallback = () => {
    if (fallback) {
      return <div className={styles.fallback}>{fallback}</div>;
    }

    if (hasError) {
      return (
        <div
          className={styles.fallback}
          style={{
            width: width || "100%",
            height: height || "200px",
            backgroundColor: "#ffffff",
            color: "#666",
            fontSize: "14px",
          }}
        >
          Failed to load image
        </div>
      );
    }

    return null;
  };

  return (
    <div
      ref={ref}
      className={cx(styles.container, className)}
      style={{
        ...style,
        width: width || "100%",
        height: height || "100%",
        position: style?.position || "relative",
      }}
    >
      {inView && src ? (
        <>
          {/* Show skeleton while loading */}
          {isLoading && (
            <div
              className={cx(styles.skeleton, styles.fallback)}
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#ffffff",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
                borderRadius: "inherit",
              }}
            />
          )}

          {/* Image - always render but control visibility */}
          <img
            alt={alt}
            src={src}
            className={styles.image}
            style={{
              objectFit,
              width: "100%",
              height: "100%",
              opacity: isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
              position:
                style?.position === "absolute" ? "absolute" : "relative",
              top: style?.top || 0,
              left: style?.left || 0,
              zIndex: isLoading ? -1 : 2,
              borderRadius: "inherit",
            }}
            loading="lazy"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />

          {/* Show fallback if image failed to load */}
          {hasError && (
            <div
              className={styles.fallback}
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#ffffff",
                color: "#666",
                fontSize: "14px",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 3,
                borderRadius: "inherit",
              }}
            >
              Failed to load image
            </div>
          )}
        </>
      ) : (
        /* Show skeleton when not in view */
        <div
          className={cx(styles.skeleton, styles.fallback)}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#ffffff",
            position: "absolute",
            top: 0,
            left: 0,
            borderRadius: "inherit",
          }}
        />
      )}
    </div>
  );
};

export default GImage;
